/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PatternFormat } from 'react-number-format';

import { Button } from '../../../../components/core';
import { CreatePaylinkCustomerBody } from '../../../../store/modules/merchant/types';
import { CustomerFieldType, sanitizeNumericString } from '../../SendPaylinkForm/formValues';
import { PaylinkCustomerInfoResponse } from '../../../../store/modules/paylink/types';
import { Alert } from '../Alert';
import { DialogCloseButton } from '../../../../components/core/DialogCloseButton';

interface Props {
  onSubmit: (customer: Partial<CreatePaylinkCustomerBody>) => Promise<any>;
  isOpen: boolean;
  onClose: () => void;
  serverValidationError?: string;
  type: 'Add' | 'Edit';
  customer?: PaylinkCustomerInfoResponse;
}

type FormValues = z.infer<typeof validationSchema>;

const validationSchema = z
  .object({
    firstName: z.string().nonempty('Customer First Name is required'),
    lastName: z.string().nonempty('Customer Last Name is required'),
    [CustomerFieldType.EMAIL]: z.string().optional(),
    [CustomerFieldType.PHONE]: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data[CustomerFieldType.PHONE]?.length &&
      !z
        .string()
        .regex(/^\d{10}$/)
        .safeParse(sanitizeNumericString(data[CustomerFieldType.PHONE] as string)).success
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid phone number',
        path: [CustomerFieldType.PHONE],
      });
    }
  });

const CustomerForm = ({
  onSubmit,
  onClose,
  serverValidationError,
  type,
  customer,
}: Pick<Props, 'onSubmit' | 'onClose' | 'serverValidationError' | 'type' | 'customer'>) => {
  const formDefaultValues: Partial<FormValues> = {
    firstName: customer?.firstName || '',
    lastName: customer?.lastName || '',
    [CustomerFieldType.EMAIL]: customer?.email || '',
    [CustomerFieldType.PHONE]: customer?.phone || '',
  };

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: formDefaultValues,
    reValidateMode: 'onBlur',
  });

  const onSubmitWithEffects = async (customerInfo: Partial<CreatePaylinkCustomerBody>) => {
    setIsProcessing(true);
    try {
      await onSubmit(customerInfo);
    } finally {
      setIsProcessing(false);
    }
    onClose();
  };

  const onFormSubmit = async (data: FormValues) => {
    setErrorMessage(undefined);
    if (
      (!data[CustomerFieldType.EMAIL] || data[CustomerFieldType.EMAIL] === '') &&
      (!data[CustomerFieldType.PHONE] || data[CustomerFieldType.PHONE] === '')
    ) {
      setErrorMessage('An email and/or a phone number is required in order to save a Customer.');
      return;
    }

    const { firstName, lastName, customerEmail, customerPhone } = data;
    const body: Partial<CreatePaylinkCustomerBody> = {
      firstName,
      lastName,
      ...(customerEmail?.length && { email: customerEmail }),
      ...(customerPhone?.length && { phone: sanitizeNumericString(customerPhone) }),
    };

    await onSubmitWithEffects(body);
  };

  return (
    <>
      {[errorMessage, serverValidationError].map(
        (e) =>
          e && (
            <Alert sx={{ mb: 4 }} severity="error" key={e.toString()}>
              {e}
            </Alert>
          ),
      )}
      <Stack component="form" onSubmit={handleSubmit(onFormSubmit)} id="add-customer">
        <Stack spacing={2}>
          <Stack sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 1 } }}>
            <TextField
              variant="outlined"
              label="Customer First Name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              required
              id="firstName"
              {...register('firstName')}
            />
            <TextField
              variant="outlined"
              label="Customer Last Name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              required
              id="lastName"
              {...register('lastName')}
            />
          </Stack>
          <TextField
            variant="outlined"
            label="Email"
            type="email"
            error={!!errors[CustomerFieldType.EMAIL]}
            helperText={errors[CustomerFieldType.EMAIL]?.message}
            id={CustomerFieldType.EMAIL}
            {...register(CustomerFieldType.EMAIL)}
          />
          <Controller
            name={CustomerFieldType.PHONE}
            control={control}
            render={({ field }) => (
              <PatternFormat
                customInput={TextField}
                variant="outlined"
                label="Phone number"
                format="(###) ###-####"
                error={!!errors[CustomerFieldType.PHONE]}
                helperText={errors[CustomerFieldType.PHONE]?.message}
                onChange={field.onChange}
                value={field.value}
                name={field.name}
                id={field.name}
              />
            )}
          />
        </Stack>
        <Divider sx={{ mt: 4, mb: 2 }} />
        <Stack direction="row" spacing={1}>
          <Button text="Cancel" theme="secondary" onClick={onClose} isFullWidth />
          <Button
            text={type === 'Add' ? 'Add Customer' : 'Save'}
            form="add-customer"
            loading={isProcessing}
            disabled={isProcessing}
            type="submit"
            isFullWidth
          />
        </Stack>
      </Stack>
    </>
  );
};

const CustomerModal = ({
  isOpen,
  onClose,
  serverValidationError,
  type,
  customer,
  onSubmit,
}: Props) => {
  return (
    <Dialog open={isOpen} fullWidth onClose={onClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'black' }}>
          {type} Customer
        </Typography>
      </DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent sx={{ py: 2, px: 3, overflowX: 'hidden' }} dividers>
        <CustomerForm
          onSubmit={onSubmit}
          onClose={onClose}
          serverValidationError={serverValidationError}
          type={type}
          customer={customer}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CustomerModal;
